import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import {
    getLeadDetail,
    moveLeadToStage,
    registerLeadOutcome,
    assignLeadConsultant,
    reopenLead as reopenLeadService,
    archiveLead as archiveLeadService
} from "../../../../services/platform_lead_service";

const usePlatformLeadDetail = (idTenant) => {
    const [lead, setLead] = useState(null);
    const [interactions, setInteractions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchDetail = useCallback(async () => {
        if (!idTenant) return;
        try {
            setLoading(true);
            setError(null);
            const data = await getLeadDetail(idTenant);
            setLead(data.lead || null);
            setInteractions(data.interactions || []);
        } catch (err) {
            console.error("[usePlatformLeadDetail] Erro ao carregar detalhes do lead:", err);
            setError(err.message || "Erro ao carregar detalhes.");
        } finally {
            setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    const changeStage = async (targetStage, notes = "") => {
        try {
            setLoading(true);
            await moveLeadToStage({ idTenant, targetStage, notes });
            toast.success("Estágio atualizado com sucesso!");
            await fetchDetail();
        } catch (err) {
            console.error("[usePlatformLeadDetail] Erro ao alterar estágio:", err);
            toast.error(err.message || "Erro ao alterar estágio.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const addOutcome = async ({ outcome, notes = "", channel = "whatsapp", lossReason = null }) => {
        try {
            setLoading(true);
            await registerLeadOutcome({ idTenant, outcome, notes, channel, lossReason });
            toast.success("Contato registrado com sucesso!");
            await fetchDetail();
        } catch (err) {
            console.error("[usePlatformLeadDetail] Erro ao registrar contato:", err);
            toast.error(err.message || "Erro ao registrar contato.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const assignConsultantToLead = async (consultant) => {
        try {
            setLoading(true);
            await assignLeadConsultant({ idTenant, consultant });
            toast.success("Consultor atribuído com sucesso!");
            await fetchDetail();
        } catch (err) {
            console.error("[usePlatformLeadDetail] Erro ao atribuir consultor:", err);
            toast.error(err.message || "Erro ao atribuir consultor.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const reopenLead = async ({ reopenReason, notes = "" }) => {
        try {
            setLoading(true);
            await reopenLeadService({ idTenant, reopenReason, notes });
            toast.success("Lead reaberto! Voltou para 'Primeiro Contato'.");
            await fetchDetail();
        } catch (err) {
            console.error("[usePlatformLeadDetail] Erro ao reabrir lead:", err);
            toast.error(err.message || "Erro ao reabrir lead.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const archiveLead = async () => {
        try {
            setLoading(true);
            await archiveLeadService({ idTenant });
            toast.success("Lead arquivado.");
            await fetchDetail();
        } catch (err) {
            console.error("[usePlatformLeadDetail] Erro ao arquivar lead:", err);
            toast.error(err.message || "Erro ao arquivar lead.");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    return {
        lead,
        interactions,
        loading,
        error,
        changeStage,
        addOutcome,
        assignConsultantToLead,
        reopenLead,
        archiveLead,
        refetch: fetchDetail
    };
};

export default usePlatformLeadDetail;
